import { useState, useEffect, useCallback, useRef } from "react";
import useShowMessages from "./useShowMessages";
import useLoading from "./useLoading";

/**
 * A hook to manage the state and logic for settings exclusions
 * @param {object} config - Configuration object for the hook
 * @param {object} config.settingSelected - The initial settings passed from the parent
 * @param {Function} config.parseInitialData - Function to parse the data from `settingSelected.value`
 * @param {Function} config.calculateDiff - Function to see the difference between initial and current state for saving
 * @param {Function} config.updateSetting - Function to call to save changes on components
 * @param {Function} config.onClose - Function to close the modal.
 * @param {Function} config.openSuccessModal - Function to open the modal on save.
 * @returns {object}
 */

export const useSettingsExclusion = ({
    settingSelected,
    parseInitialData,
    calculateDiff,
    updateSetting,
    onClose,
    openSuccessModal,
}) => {
    const [items, setItems] = useState([]);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const { showMessage } = useShowMessages();
    const { isLoading, setIsLoading } = useLoading();
    const initialItemsRef = useRef([]);

    useEffect(() => {
        if (settingSelected?.value) {
            const parsedData = parseInitialData(settingSelected.value);
            setItems(parsedData);
            initialItemsRef.current = parsedData;
        }
    }, [settingSelected, parseInitialData]);

    const handleSelectionChange = useCallback((ids) => {
        setSelectedRowIds(ids.map(Number));
    }, []);

    const handleRemoveSelected = useCallback(() => {
        setItems((prev) => prev.filter((item) => !selectedRowIds.includes(item.id)));
        setSelectedRowIds([]);
    }, [selectedRowIds]);

    const handleSave = useCallback(async () => {
        const diff = calculateDiff(initialItemsRef.current, items);
        if (diff.length === 0) {
            showMessage("No changes to save.", "info");
            onClose();
            return;
        }

        setIsLoading(true);
        try {
            await updateSetting(diff);
            setIsLoading(false);
            openSuccessModal();
            onClose();
        } catch (error) {
            console.error("Error saving settings:", error);
            setIsLoading(false);
            showMessage("Error saving settings.", "error");
        }
    }, [items, calculateDiff, updateSetting, openSuccessModal, onClose, showMessage, setIsLoading]);

    return {
        isLoading,
        items,
        setItems,
        paginationModel,
        setPaginationModel,
        selectedRowIds,
        handleSelectionChange,
        handleRemoveSelected,
        handleSave,
    };
};